/**
 * RTWO Asset & Token Engine — Asset Registry Service
 */

import type { AssetId, RTWOAssetDefinition, RTWOAssetType } from '../domain-models';
import type { AssetRegistryResult, IAssetRegistryService } from '../service-interfaces';
import type { ASSRepositories } from '../repositories/ass-repositories';
import type { LedgerAdapterRegistry } from '../registry/adapter-registries';
import { ASSTInternalEventTypes } from '../events/ass-event-bus';
import { BaseASSService, type ASSServiceContext } from './base-ass-service';

export class AssetRegistryService extends BaseASSService implements IAssetRegistryService {
  readonly serviceId = 'ass-asset-registry-001';
  readonly serviceName = 'Asset Registry Service';
  readonly description =
    'Asset registry — placeholder, requires real Ledger Connection. No fake assets.';
  readonly integrations = ['Core Engine', 'Ledger Engine', 'System Registry', 'Indexer Engine'];
  readonly requiresLedgerConnection = true;
  readonly requiresTokenRuntime = false;

  constructor(
    context: ASSServiceContext,
    private readonly repositories: ASSRepositories,
    private readonly ledgerRegistry: LedgerAdapterRegistry
  ) {
    super(context);
  }

  async initialize(): Promise<void> {
    this.status = 'waiting_for_ledger_connection';
  }

  async registerAsset(
    assetSymbol: string,
    assetName: string,
    assetType: RTWOAssetType,
    decimals: number,
    issuerReference: string
  ): Promise<AssetRegistryResult> {
    if (!this.ledgerRegistry.isConfigured()) {
      return {
        success: false,
        assetId: null,
        error: 'Asset registration requires real Ledger Connection — Ledger Connection Pending',
        isPlaceholder: true,
        source: 'Asset & Token Engine — Ledger Connection Pending',
      };
    }

    const adapterResult = await this.ledgerRegistry
      .getActiveAdapter()
      .registerAsset(assetSymbol, assetName, assetType, decimals, issuerReference);

    if (adapterResult?.asset) {
      this.repositories.assets.save(adapterResult.asset);
      this.context.eventBus.emit(ASSTInternalEventTypes.ASSET_REGISTERED, {
        assetId: adapterResult.asset.assetId,
      });
    }

    return {
      success: false,
      assetId: null,
      error: 'Asset registration requires real Ledger Connection — Ledger Connection Pending',
      isPlaceholder: true,
      source: 'Asset & Token Engine — Ledger Connection Pending',
    };
  }

  getAllAssets(): RTWOAssetDefinition[] {
    if (!this.ledgerRegistry.isConfigured()) return [];
    return this.repositories.assets.findAll();
  }

  getAsset(assetId: AssetId): RTWOAssetDefinition | null {
    if (!this.ledgerRegistry.isConfigured()) return null;
    return this.repositories.assets.findById(assetId);
  }

  getAssetsByType(assetType: RTWOAssetType): RTWOAssetDefinition[] {
    if (!this.ledgerRegistry.isConfigured()) return [];
    return this.repositories.assets.findByType(assetType);
  }

  getAssetCount(): 0 {
    return 0;
  }
}
